import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { Checkbox } from '@tecuity/granite-ui/Checkbox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import SelectableCheckboxList, {
  CheckboxList,
} from '../SettingsViews/SettingsComponents/SelectableCheckboxList'
import * as api from '../../../requests'
import '../SettingsViews/SettingsListView.scss'
import { setErrorToast, setSuccessToast } from '../../../actions'
import { useRequestData } from '../../../Utilities/hooks'
import { Spinner } from '../../Loaders/Loaders'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import Revert from '@tecuity/sos-icons/react/revert'
import RightArrow from '@tecuity/sos-icons/react/right-arrow'
import { getSourceTypeId } from '../../../Utilities/Utilities'

const TrademarkFilingToRecordTypeXref = () => {
  const dispatch = useDispatch()
  const imagePageTypes = useRequestData(api.GET_ALL_IMAGE_PAGE_TYPES, [], [])
  const recordAccessUserRoles = useRequestData(
    (cb, errCb) => {
      api.GET_RECORD_ACCESS_USER_ROLES_BY_SOURCE_TYPE(getSourceTypeId('trademark'), cb, errCb)
    },
    [],
    []
  )
  const formData = useRequestData(
    (cb, errCb) => api.GET_ALL_FORMS_BY_CATEGORY('Trademark', cb, errCb),
    [],
    []
  )
  const appReports = useRequestData(api.GET_APP_REPORTS, [], [])
  const durationTermTypes = useRequestData(
    (cb, errCb) => api.GET_ACTIVE_BASIC_LOOKUP_TYPES('', 'DurationTermType', cb, errCb),
    [],
    []
  )
  const expirationReferenceTypes = useRequestData(
    (cb, errCb) => api.GET_ACTIVE_BASIC_LOOKUP_TYPES('', 'ExpirationReferenceType', cb, errCb),
    [],
    []
  )
  const [recordTypeOptions, setRecordTypeOptions] = useState([])
  const [filingTypeList, setFilingTypeList] = useState([])
  const [statusTypeOptions, setStatusTypeOptions] = useState([])
  const [recordType, setRecordType] = useState(0)
  const [activeFilingId, setActiveFilingId] = useState(0)
  const [originalIds, setOriginalIds] = useState([])
  const [checkedFilingTypeIds, setCheckedFilingTypeIds] = useState([])
  const [xRef, setXRef] = useState({ ...defaultDto })
  const [xRefMap] = useState({})
  const [loadingXref, setLoadingXref] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    getLookupTypeOptions()
  }, [])

  const getLookupTypeOptions = () => {
    api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
      'Trademark',
      'RecordType',
      (res) => {
        setRecordTypeOptions([
          ...res.map((t) => ({
            value: t.Id,
            label: t.Name,
          })),
        ])
        if (!recordType && res.length > 0) setRecordType(res[0].Id)
      },
      (err) => {
        setRecordTypeOptions([])
        console.error(err.message)
      }
    )
    api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
      'Trademark',
      'FilingType',
      (res) => {
        setFilingTypeList(res)
      },
      (err) => {
        setFilingTypeList([])
        console.error(err.message)
      }
    )
    api.GET_ACTIVE_BASIC_LOOKUP_TYPES(
      'Trademark',
      'StatusType',
      (res) => {
        setStatusTypeOptions(res)
      },
      (err) => {
        setStatusTypeOptions([])
        console.error(err.message)
      }
    )
  }

  const getTrademarkFilingTypeIdsByRecordType = (controller) => {
    if (recordType) {
      api.GET_SETTINGS_TRADEMARK_FILING_TYPE_IDS_BY_RECORD_TYPE(
        { signal: controller.signal },
        recordType,
        (res) => {
          if (res) {
            setCheckedFilingTypeIds(res)
            setOriginalIds(res)
          }
        },
        (err) => {
          console.error(err)
          dispatch(setErrorToast('Failed to load selected Filing Type Ids'))
        }
      )
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    getTrademarkFilingTypeIdsByRecordType(controller)
    return () => {
      controller.abort()
    }
  }, [recordType])

  const getXrefByRecordTypeFilingType = (controller) => {
    if (recordType && activeFilingId) {
      setLoadingXref(true)
      api.GET_SETTINGS_TRADEMARK_FILING_TO_RECORD_XREF(
        { signal: controller.signal },
        recordType,
        activeFilingId,
        (res) => {
          if (isDirty && Object.keys(xRefMap).includes(activeFilingId.toString())) {
            setXRef({ ...xRefMap[activeFilingId] })
          } else if (res) {
            setXRef({
              ...res,
              TrademarkRecordTypeId: recordType,
              TrademarkFilingTypeId: activeFilingId,
              PriorTrademarkStatusIds: res.PriorTrademarkStatusIdsJson
                ? JSON.parse(res.PriorTrademarkStatusIdsJson)
                : [],
              AppReportIds: res.ReportConfigJson ? JSON.parse(res.ReportConfigJson) : [],
            })
          } else {
            setXRef({
              ...defaultDto,
              TrademarkRecordTypeId: recordType,
              TrademarkFilingTypeId: activeFilingId,
            })
          }
          setLoadingXref(false)
        },
        (err) => {
          console.error(err)
          if (err.message !== 'Xref not found') dispatch(setErrorToast('Failed to load Xref'))
          setXRef({
            ...defaultDto,
            TrademarkRecordTypeId: recordType,
            TrademarkFilingTypeId: activeFilingId,
          })
          setLoadingXref(false)
        }
      )
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    getXrefByRecordTypeFilingType(controller)
    return () => {
      controller.abort()
    }
  }, [recordType, activeFilingId])

  const clickRevert = () => {
    Object.keys(xRefMap).forEach((key) => {
      delete xRefMap[key]
    })
    setIsDirty(false)
    getTrademarkFilingTypeIdsByRecordType(new AbortController())
    getXrefByRecordTypeFilingType(new AbortController())
    getLookupTypeOptions()
  }

  const clickSave = () => {
    const promiseArray = []
    delete xRefMap['0']
    const changedFilingTypeIds = Object.keys(xRefMap)

    changedFilingTypeIds.forEach((filingTypeId) => {
      if (originalIds.includes(+filingTypeId)) {
        if (checkedFilingTypeIds.includes(+filingTypeId)) {
          // update the existing
          promiseArray.push(
            new Promise((resolve, reject) => {
              api.SAVE_SETTINGS_TRADEMARK_FILING_TYPE_RECORD_TYPE_XREF(
                'PUT',
                xRefMap[filingTypeId],
                resolve,
                reject
              )
            })
          )
        } else {
          // delete the existing
          promiseArray.push(
            new Promise((resolve, reject) => {
              api.DELETE_SETTINGS_TRADEMARK_FILING_TYPE_RECORD_TYPE_XREF(
                recordType,
                filingTypeId,
                resolve,
                reject
              )
            })
          )
        }
      } else if (checkedFilingTypeIds.includes(+filingTypeId)) {
        // add new xref
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.SAVE_SETTINGS_TRADEMARK_FILING_TYPE_RECORD_TYPE_XREF(
              'POST',
              xRefMap[filingTypeId],
              resolve,
              reject
            )
          })
        )
      } else {
        // nothing changed
        console.warn('Trademark Xref data out of sync. This should NOT happen.')
      }
    })

    if (promiseArray.length > 0) {
      setLoadingXref(true)
      Promise.all(promiseArray)
        .then((xrefs) => {
          dispatch(setSuccessToast('Saved Setting(s) successfully.'))
          clickRevert()
          setLoadingXref(false)
        })
        .catch((err) => {
          if (err.DisplayToUser === true && (err.Message || '') !== '') {
            dispatch(setErrorToast(err.Message))
          } else {
            dispatch(setErrorToast('Failed to save setting.'))
          }
          setLoadingXref(false)
        })
    }
  }

  const disableXrefSettings = useMemo(() => {
    if (loadingXref) return true
    return !checkedFilingTypeIds.includes(activeFilingId)
  }, [activeFilingId, checkedFilingTypeIds, loadingXref])

  const changeXrefValue = (property, value) => {
    setXRef((ref) => ({ ...ref, [property]: value }))
    setIsDirty(true)
  }

  useEffect(() => {
    xRefMap[xRef.TrademarkFilingTypeId] = { ...xRef }
  }, [xRef])

  return (
    <div className="settings-list-view">
      <h2>Trademark Filing to Record Type</h2>
      <div className="settings-content">
        <div className="settings-left-column">
          <div className="custom-correspondence-options">
            <SelectBox
              label="Record Type"
              noPlaceholder
              value={recordType || ''}
              onChange={(val) => {
                setRecordType(+val)
                setActiveFilingId(0)
              }}
              options={recordTypeOptions}
              disabled={isDirty ? true : undefined}
              description={isDirty ? 'Save or Revert changes to modify.' : undefined}
            />
          </div>
          <SelectableCheckboxList
            label={`Filing Types`}
            items={filingTypeList.map((type) => {
              return {
                id: type.Id,
                label: type.Name,
              }
            })}
            activeId={activeFilingId}
            setActiveId={setActiveFilingId}
            checked={checkedFilingTypeIds}
            setChecked={(ids) => {
              setCheckedFilingTypeIds(ids)
              setIsDirty(true)
            }}
          />
        </div>
        <div className="properties-list">
          <h3
            style={{
              textDecoration: 'underline',
              display: 'flex',
              alignSelf: 'center',
              fontWeight: 'bold',
            }}
          >
            Details
          </h3>
          <p>
            To use this page, a check box indicates that there is a saved xref for that filing type.
            To save one, the user must click on the Filing Type. Then proceed to check the box, that
            will allow the Options on the right side to work. Then proceed to fill it out and click
            save to save that Xref. To delete an Xref, uncheck the box and hit Save.
          </p>
          <SelectBox
            label="Image Page Type"
            options={imagePageTypes
              .map((imagePageType) => {
                return {
                  label: imagePageType.Name,
                  value: imagePageType.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('ImagePageTypeId', +value)
            }}
            value={xRef.ImagePageTypeId || ''}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Form Title"
            options={formData
              .filter((form) => {
                return form.IsActive
              })
              .map((form) => {
                return {
                  label: form.Title,
                  value: form.FormDefinitionId,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('FormDefinitionId', +value)
            }}
            value={xRef.FormDefinitionId || ''}
            disabled={disableXrefSettings}
          />

          <h3
            style={{
              textDecoration: 'underline',
              display: 'flex',
              alignSelf: 'center',
              fontWeight: 'bold',
            }}
          >
            Requirements to File
          </h3>

          <CheckboxList
            label="Record Access Roles Permitted to File (Leave blank to allow open filing)"
            items={recordAccessUserRoles
              .filter((x) => x.CanFileAmendments)
              .map((role) => ({
                id: role.RecordAccessUserRoleId,
                label: role.Name,
              }))
              .sort((a, b) => (a.label > b.label ? 1 : -1))}
            checkedIds={xRef.PermittedRecordAccessUserRoleIds || []}
            setChecked={(ids) => {
              changeXrefValue('PermittedRecordAccessUserRoleIds', ids)
            }}
          />

          <CheckboxList
            label={'Status(s) to File'}
            items={statusTypeOptions
              .map((status) => ({ id: status.Id, label: status.Name }))
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            checkedIds={xRef.PriorTrademarkStatusIds || []}
            setChecked={(ids) => {
              changeXrefValue('PriorTrademarkStatusIds', ids)
              changeXrefValue('PriorTrademarkStatusIdsJson', JSON.stringify(ids))
            }}
            disabled={disableXrefSettings}
          />

          <TextInput
            label={`Maximum Number of Days Before Expiration this can be Filed`}
            value={xRef.MaxDaysBeforeExpiration || ''}
            onChange={(e) => {
              changeXrefValue(
                'MaxDaysBeforeExpiration',
                e.target.value === '' ? null : e.target.value
              )
            }}
            disabled={disableXrefSettings}
            type="number"
          />

          <h3
            style={{
              textDecoration: 'underline',
              display: 'flex',
              alignSelf: 'center',
              fontWeight: 'bold',
            }}
          >
            Results of Approved Filing
          </h3>

          <SelectBox
            label="Record Access Role Granted Post-Approval"
            options={recordAccessUserRoles
              .map((role) => {
                return {
                  label: role.Name,
                  value: role.RecordAccessUserRoleId,
                }
              })
              .sort((a, b) => {
                return a.value > b.value ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue(
                'NewRecordAccessUserRoleId',
                value === '' ? null : +value
              )
            }}
            value={xRef.NewRecordAccessUserRoleId || ''}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Trademark Status Post-Approval"
            options={[...statusTypeOptions, { Name: 'No Change', Id: -1 }]
              .map((status) => {
                return {
                  label: status.Name,
                  value: status.Id,
                }
              })
              .sort((a, b) => {
                return a.label === 'No Change' ? -1 : a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('NewTrademarkStatusId', +value)
            }}
            value={xRef.NewTrademarkStatusId || -1}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Duration Term"
            options={durationTermTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('DurationTermTypeId', +value)
            }}
            value={xRef.DurationTermTypeId || '0'}
            disabled={disableXrefSettings}
          />

          <SelectBox
            label="Expiration Reference"
            options={expirationReferenceTypes
              .map((cd) => {
                return {
                  label: cd.Name,
                  value: cd.Id,
                }
              })
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            onChange={(value) => {
              changeXrefValue('ExpirationReferenceTypeId', +value)
            }}
            value={xRef.ExpirationReferenceTypeId || '0'}
            disabled={disableXrefSettings}
          />

          <Checkbox
            label="Include Original Image with Approval Stamp for Paper Filings"
            checked={xRef.IncludeImageForPaperFiling || false}
            onChange={(checked) => {
              changeXrefValue('IncludeImageForPaperFiling', checked)
            }}
            disabled={disableXrefSettings}
          />

          <CheckboxList
            label={'Generated Reports'}
            items={appReports
              .map((appReport) => ({ id: appReport.Id, label: appReport.Name }))
              .sort((a, b) => {
                return a.label > b.label ? 1 : -1
              })}
            checkedIds={xRef.AppReportIds || []}
            setChecked={(ids) => {
              changeXrefValue('AppReportIds', ids)
              changeXrefValue('ReportConfigJson', JSON.stringify(ids))
            }}
            disabled={disableXrefSettings}
          />

          <div className="button-wrapper">
            {loadingXref && (
              <div className="loading-type">
                <Spinner size="small" themeColor="dark-neutral" />
              </div>
            )}
            <ContextButton
              variant="danger"
              disabled={!isDirty}
              leftIcon={
                <div className="checkmark-wrapper left">
                  <Revert />
                </div>
              }
              onClick={clickRevert}
            >
              Revert
            </ContextButton>
            <ContextButton
              variant="primary"
              disabled={!isDirty}
              rightIcon={
                <div className="checkmark-wrapper right">
                  <RightArrow />
                </div>
              }
              onClick={clickSave}
            >
              Save
            </ContextButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TrademarkFilingToRecordTypeXref

const defaultDto = {
  TrademarkFilingTypeId: 0, //done
  TrademarkRecordTypeId: 0, //done
  PriorTrademarkStatusIds: [],
  NewTrademarkStatusId: 0,
  FormDefinitionId: null, //done
  DurationTermTypeId: 0, //done
  ExpirationReferenceTypeId: 0, //done
  MaxDaysBeforeExpiration: 0,
  IncludeImageForPaperFiling: false,
  AppReportIds: [], // done
  ImagePageTypeId: null, //done
  RequiredRecordOwnerTypeId: null, //done
  NewRecordOwnerTypeId: null, //done
  PermittedRecordAccessUserRoleIds: [],
  NewRecordAccessUserRoleId: null,
}
